/**
 * Ввод ключей сервисов прямо в блоке сервиса. Значение уходит на сервер, проверяется и хранится
 * зашифрованным; обратно приходят только последние символы («…a1B2») — скопировать ключ нельзя.
 */
import type { SecretName, SecretsStatus } from '@jt/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { ApiError, get, put } from '../api/client';
import { ConfirmButton, useUI } from '../components/overlays';
import { useIsAdmin } from '../api/hooks';
import { integrationsKey } from '../pages/integrations';

export const secretsKey = ['secrets'] as const;

export const useSecrets = () => {
  const admin = useIsAdmin();
  return useQuery({
    queryKey: secretsKey,
    queryFn: () => get<SecretsStatus>('/integrations/secrets'),
    staleTime: 30_000,
    enabled: admin,
  });
};

/** Сохранить часть ключей; возвращает текст ошибки или null. */
export function useSaveSecrets() {
  const qc = useQueryClient();
  return async (patch: Partial<Record<SecretName, string>>): Promise<string | null> => {
    try {
      qc.setQueryData(secretsKey, await put<SecretsStatus>('/integrations/secrets', patch));
      await qc.invalidateQueries({ queryKey: integrationsKey });
      return null;
    } catch (e) {
      return e instanceof ApiError ? e.message : 'Не сохранилось. Повторите.';
    }
  };
}

export function SecretsReadOnlyNote() {
  const q = useSecrets();
  if (q.data?.writable !== false) return null;
  return (
    <p className="muted sm">
      На сервере не задан APP_ENCRYPTION_KEY — ключи можно указать только в deploy/.env.
    </p>
  );
}

export function SecretField({
  name,
  label,
  placeholder,
  help,
  info,
  secret = true,
  numeric = false,
}: {
  name: SecretName;
  label: string;
  placeholder?: string;
  help?: ReactNode;
  /** Подробная инструкция «где взять» — по кнопке «i» у названия поля. */
  info?: ReactNode;
  /** false — обычное поле: значение показывается целиком. */
  secret?: boolean;
  /** Только цифры (api_id): цифровая клавиатура на телефоне. */
  numeric?: boolean;
}) {
  const ui = useUI();
  const q = useSecrets();
  const save = useSaveSecrets();
  const id = useId();
  const st = q.data?.items[name];
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  if (!q.data || !st) return null;
  const writable = q.data.writable;
  const open = editing || !st.source;

  const submit = async (v: string) => {
    setBusy(true);
    setError(null);
    const err = await save({ [name]: v });
    setBusy(false);
    if (err) return setError(err);
    setEditing(false);
    setValue('');
    ui.toast(v ? 'Проверено и сохранено' : 'Убрано из интерфейса');
  };

  return (
    <div className="secret">
      <div className="secret-h">
        <span className="secret-l">
          <label htmlFor={id}>{label}</label>
          {info && (
            <button
              type="button"
              className="info-btn"
              aria-label={`Как получить: ${label}`}
              aria-expanded={showInfo}
              aria-controls={`${id}-info`}
              onClick={() => setShowInfo(!showInfo)}
            >
              i
            </button>
          )}
        </span>
        <span className={`sm ${st.source ? 'ok-text' : 'muted'}`}>
          {st.source === 'ui'
            ? `задан${st.hint ? ` · ${st.hint}` : ''}`
            : st.source === 'env'
              ? `задан на сервере (deploy/.env)${st.hint ? ` · ${st.hint}` : ''}`
              : 'не задан'}
        </span>
      </div>
      {info && showInfo && (
        <div className="info-pop" id={`${id}-info`} role="note">
          {info}
        </div>
      )}
      {open && writable ? (
        <form
          className="secret-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) void submit(value);
          }}
        >
          <input
            id={id}
            type={secret ? 'password' : 'text'}
            inputMode={numeric ? 'numeric' : undefined}
            autoComplete={secret ? 'new-password' : 'off'}
            spellCheck={false}
            autoCapitalize="off"
            value={value}
            placeholder={placeholder}
            aria-invalid={error ? true : undefined}
            onChange={(e) => setValue(e.target.value)}
          />
          <button className="btn primary" type="submit" disabled={busy || !value.trim()}>
            {busy ? 'Проверяю…' : secret ? 'Проверить и сохранить' : 'Сохранить'}
          </button>
          {editing && (
            <button
              className="btn ghost"
              type="button"
              onClick={() => {
                setEditing(false);
                setValue('');
                setError(null);
              }}
            >
              Отмена
            </button>
          )}
        </form>
      ) : (
        writable && (
          <div className="btnrow">
            <button className="btn sm" type="button" onClick={() => setEditing(true)}>
              Заменить
            </button>
            {st.source === 'ui' && (
              <ConfirmButton
                className="btn sm ghost danger"
                confirmText="Убрать ключ?"
                onConfirm={() => void submit('')}
              >
                Убрать
              </ConfirmButton>
            )}
          </div>
        )
      )}
      {error && (
        <p className="sm" role="alert" style={{ color: 'var(--danger)', margin: '6px 0 0' }}>
          {error}
        </p>
      )}
      {help && open && <p className="muted sm secret-help">{help}</p>}
    </div>
  );
}
